import {
  NOTICE_DISCLOSURE_IDS,
  NOTICE_DISCLOSURE_SCHEMA_VERSION,
  parseNoticeDisclosureProjectionV1,
  requireNoticeDisclosureId,
} from "./notice-disclosure-contract.js";
import {
  createBrowserNoticeDisclosureLocalProvider,
} from "./notice-disclosure-local-provider.js";

const MAX_DOCUMENT_CHARS = 4_096;
const DOCUMENT_FIELDS = Object.freeze([
  "disclosures",
  "revision",
  "schemaVersion",
]);
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.notice-disclosure-preference.v1",
);
const NOTICE_ID_SET = new Set(NOTICE_DISCLOSURE_IDS);

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function exactFields(value, expected) {
  const fields = Object.keys(value).sort();
  const sortedExpected = [...expected].sort();
  return fields.length === sortedExpected.length
    && fields.every((field, index) => field === sortedExpected[index]);
}

function freezeDisclosures(disclosures) {
  return Object.freeze(Object.fromEntries(
    NOTICE_DISCLOSURE_IDS
      .filter((noticeId) => Object.hasOwn(disclosures, noticeId))
      .map((noticeId) => [noticeId, disclosures[noticeId]]),
  ));
}

function freezeState(value) {
  const next = { ...value };
  if (next.disclosures) next.disclosures = freezeDisclosures(next.disclosures);
  if (next.authoritative) next.authoritative = freezeState(next.authoritative);
  if (next.legacyCleanup) next.legacyCleanup = Object.freeze({ ...next.legacyCleanup });
  return Object.freeze(next);
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function unavailable(operation, reason, authoritative = null) {
  return freezeState({
    kind: "unavailable",
    operation,
    reason,
    ...(authoritative ? { authoritative } : {}),
  });
}

function expectedRevisionFrom(options) {
  if (options === undefined) return undefined;
  if (!isRecord(options) || Object.keys(options).some((field) => field !== "expectedRevision")) {
    throw new TypeError("Notice disclosure command options contain unsupported fields.");
  }
  const revision = options.expectedRevision;
  if (revision === undefined || revision === null) return revision;
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError("Notice disclosure expected revision must be null or positive.");
  }
  return revision;
}

function decodeDisclosures(value) {
  if (!isRecord(value)) {
    return { ok: false, reason: "disclosures-must-be-an-object" };
  }
  const fields = Object.keys(value);
  if (fields.length === 0) {
    return { ok: false, reason: "disclosures-must-not-be-empty" };
  }
  if (fields.some((noticeId) => !NOTICE_ID_SET.has(noticeId))) {
    return { ok: false, reason: "disclosure-id-is-not-supported" };
  }
  if (fields.some((noticeId) => typeof value[noticeId] !== "boolean")) {
    return { ok: false, reason: "disclosure-values-must-be-boolean" };
  }
  return { ok: true, disclosures: freezeDisclosures(value) };
}

export function decodeNoticeDisclosurePreferenceDocument(raw) {
  if (typeof raw !== "string") {
    return freezeState({
      kind: "invalid",
      reason: "stored-value-must-be-text",
      raw,
    });
  }
  if (raw.length === 0 || raw.length > MAX_DOCUMENT_CHARS) {
    return freezeState({
      kind: "invalid",
      reason: raw.length === 0
        ? "stored-value-must-not-be-empty"
        : "stored-value-exceeds-v1-limit",
      raw,
    });
  }
  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return freezeState({ kind: "invalid", reason: "malformed-json", raw });
  }
  if (!isRecord(document)) {
    return freezeState({
      kind: "invalid",
      reason: "document-must-be-an-object",
      raw,
    });
  }
  if (
    Number.isSafeInteger(document.schemaVersion)
    && document.schemaVersion > NOTICE_DISCLOSURE_SCHEMA_VERSION
  ) {
    return freezeState({
      kind: "unsupportedVersion",
      foundVersion: document.schemaVersion,
      raw,
    });
  }
  if (!exactFields(document, DOCUMENT_FIELDS)) {
    return freezeState({
      kind: "invalid",
      reason: "document-fields-must-match-v1",
      raw,
    });
  }
  if (document.schemaVersion !== NOTICE_DISCLOSURE_SCHEMA_VERSION) {
    return freezeState({
      kind: "invalid",
      reason: "schema-version-must-be-1",
      raw,
    });
  }
  if (!Number.isSafeInteger(document.revision) || document.revision <= 0) {
    return freezeState({
      kind: "invalid",
      reason: "revision-must-be-positive",
      raw,
    });
  }
  const decoded = decodeDisclosures(document.disclosures);
  if (!decoded.ok) {
    return freezeState({ kind: "invalid", reason: decoded.reason, raw });
  }
  return freezeState({
    kind: "ready",
    revision: document.revision,
    disclosures: decoded.disclosures,
    raw,
  });
}

export function encodeNoticeDisclosurePreferenceDocument({
  revision,
  disclosures,
}) {
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError("Notice disclosure revision must be positive.");
  }
  const decoded = decodeDisclosures(disclosures);
  if (!decoded.ok) {
    throw new TypeError(`Invalid notice disclosures: ${decoded.reason}.`);
  }
  const raw = JSON.stringify({
    schemaVersion: NOTICE_DISCLOSURE_SCHEMA_VERSION,
    revision,
    disclosures: decoded.disclosures,
  });
  if (raw.length > MAX_DOCUMENT_CHARS) {
    throw new TypeError("Notice disclosure document exceeds its v1 limit.");
  }
  return raw;
}

function authoritativeState(state) {
  return state?.authoritative ?? state;
}

export function projectNoticeDisclosurePreference(
  state,
  noticeId,
  defaultOpen,
) {
  const exactNoticeId = requireNoticeDisclosureId(noticeId);
  if (typeof defaultOpen !== "boolean") {
    throw new TypeError("Notice disclosure authored default must be boolean.");
  }
  const source = isRecord(state) ? state : { kind: "uninitialized" };
  const authoritative = authoritativeState(source);
  let revision = null;
  let open = null;
  let persisted = false;
  let canPersist = false;

  if (source.kind === "pending") {
    revision = authoritative?.kind === "ready"
      ? authoritative.revision
      : source.baseRevision ?? null;
    if (
      source.noticeId === exactNoticeId
      && typeof source.requestedOpen === "boolean"
    ) {
      open = source.requestedOpen;
    } else if (authoritative?.kind === "ready") {
      open = authoritative.disclosures[exactNoticeId] ?? defaultOpen;
      persisted = Object.hasOwn(authoritative.disclosures, exactNoticeId);
    } else if (authoritative?.kind === "missing") {
      open = defaultOpen;
    }
  } else if (authoritative?.kind === "ready") {
    revision = authoritative.revision;
    open = authoritative.disclosures[exactNoticeId] ?? defaultOpen;
    persisted = Object.hasOwn(authoritative.disclosures, exactNoticeId);
    canPersist = source.kind !== "unavailable";
  } else if (authoritative?.kind === "missing") {
    open = defaultOpen;
    canPersist = source.kind !== "unavailable";
  }

  if (source.kind === "ready" || source.kind === "missing" || source.kind === "conflict") {
    canPersist = true;
  }

  return parseNoticeDisclosureProjectionV1({
    schemaVersion: NOTICE_DISCLOSURE_SCHEMA_VERSION,
    noticeId: exactNoticeId,
    kind: source.kind,
    revision,
    open,
    persisted,
    canPersist,
    warning: source.legacyCleanup ? "legacyCleanupUnavailable" : null,
  });
}

export function createNoticeDisclosurePreferenceRepository({ provider = null } = {}) {
  if (
    !provider
    || typeof provider.dispose !== "function"
    || typeof provider.readDocument !== "function"
    || typeof provider.readRetiredDisclosures !== "function"
    || typeof provider.removeDocument !== "function"
    || typeof provider.removeRetiredDisclosures !== "function"
    || typeof provider.subscribeInvalidation !== "function"
    || typeof provider.writeDocument !== "function"
  ) {
    throw new TypeError("Notice disclosure repository requires its exact local provider.");
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();

  function state() {
    return freezeState(currentState);
  }

  function deliver(listener) {
    try {
      listener(state());
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState) {
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return state();
    currentState = frozen;
    if (!disposed) {
      for (const listener of listeners) deliver(listener);
    }
    return state();
  }

  function readAuthoritativeState() {
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable(stored.operation, stored.reason);
    }
    return stored.raw === null
      ? freezeState({ kind: "missing", raw: null })
      : decodeNoticeDisclosurePreferenceDocument(stored.raw);
  }

  function cleanupRetired(authoritative) {
    const cleanup = provider.removeRetiredDisclosures();
    if (cleanup.kind !== "unavailable") return authoritative;
    return freezeState({
      ...authoritative,
      legacyCleanup: {
        operation: cleanup.operation,
        reason: cleanup.reason,
      },
    });
  }

  function writeReady(document, authoritative = null) {
    const raw = encodeNoticeDisclosurePreferenceDocument(document);
    const written = provider.writeDocument(raw);
    if (written.kind === "unavailable") {
      return publish(unavailable(
        written.operation,
        written.reason,
        authoritative,
      ));
    }
    return publish(cleanupRetired(decodeNoticeDisclosurePreferenceDocument(raw)));
  }

  function load() {
    if (disposed) return state();
    publish({ kind: "loading" });
    const authoritative = readAuthoritativeState();
    if (!["missing", "ready"].includes(authoritative.kind)) return publish(authoritative);

    const retired = provider.readRetiredDisclosures();
    if (retired.kind === "unavailable") {
      return publish(unavailable(retired.operation, retired.reason));
    }
    if (retired.kind === "missing") {
      return publish(authoritative.kind === "ready"
        ? cleanupRetired(authoritative)
        : authoritative);
    }
    if (retired.kind === "invalid") {
      if (authoritative.kind === "ready") {
        return publish({
          ...authoritative,
          legacyCleanup: {
            operation: "import",
            reason: retired.reason,
          },
        });
      }
      return publish({
        kind: "invalid",
        reason: retired.reason,
        raw: retired.raw,
        source: "retired-ui-settings",
      });
    }
    const missingRetiredDisclosures = Object.fromEntries(
      Object.entries(retired.disclosures).filter(
        ([noticeId]) => (
          authoritative.kind === "missing"
          || !Object.hasOwn(authoritative.disclosures, noticeId)
        ),
      ),
    );
    if (Object.keys(missingRetiredDisclosures).length === 0) {
      return publish(cleanupRetired(authoritative));
    }
    if (
      authoritative.kind === "ready"
      && authoritative.revision >= Number.MAX_SAFE_INTEGER
    ) {
      return publish(unavailable("write", "revision-exhausted", authoritative));
    }
    return writeReady({
      revision: authoritative.kind === "ready"
        ? authoritative.revision + 1
        : 1,
      disclosures: {
        ...(authoritative.kind === "ready" ? authoritative.disclosures : {}),
        ...missingRetiredDisclosures,
      },
    }, authoritative);
  }

  function conflict(expectedRevision, authoritative) {
    return publish({
      kind: "conflict",
      expectedRevision,
      actualRevision: authoritative.kind === "ready"
        ? authoritative.revision
        : null,
      authoritative,
    });
  }

  async function setOpen(noticeId, open, options = {}) {
    const exactNoticeId = requireNoticeDisclosureId(noticeId);
    if (typeof open !== "boolean") {
      throw new TypeError("Notice disclosure open command must be boolean.");
    }
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();
    if (!["missing", "ready"].includes(authoritative.kind)) {
      return publish(authoritative);
    }
    const currentOpen = authoritative.kind === "ready"
      ? authoritative.disclosures[exactNoticeId]
      : undefined;
    if (
      expectedRevision !== undefined
      && (
        (authoritative.kind === "missing" && expectedRevision !== null)
        || (authoritative.kind === "ready" && expectedRevision !== authoritative.revision)
      )
    ) {
      if (currentOpen === open) return publish(cleanupRetired(authoritative));
      return conflict(expectedRevision, authoritative);
    }
    if (currentOpen === open) return publish(cleanupRetired(authoritative));
    if (
      authoritative.kind === "ready"
      && authoritative.revision >= Number.MAX_SAFE_INTEGER
    ) {
      return publish(unavailable("write", "revision-exhausted", authoritative));
    }

    const baseRevision = authoritative.kind === "ready"
      ? authoritative.revision
      : null;
    const beforeRaw = authoritative.raw;
    publish({
      kind: "pending",
      noticeId: exactNoticeId,
      requestedOpen: open,
      baseRevision,
      authoritative,
    });

    const beforeWrite = provider.readDocument();
    if (beforeWrite.kind === "unavailable") {
      return publish(unavailable(beforeWrite.operation, beforeWrite.reason, authoritative));
    }
    if (beforeWrite.raw !== beforeRaw) {
      const changed = readAuthoritativeState();
      return conflict(baseRevision, changed);
    }
    return writeReady({
      revision: baseRevision === null ? 1 : baseRevision + 1,
      disclosures: {
        ...(authoritative.kind === "ready" ? authoritative.disclosures : {}),
        [exactNoticeId]: open,
      },
    }, authoritative);
  }

  async function exportRecord() {
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable("export", stored.reason);
    }
    const records = [];
    if (stored.raw !== null) {
      const decoded = decodeNoticeDisclosurePreferenceDocument(stored.raw);
      records.push(Object.freeze({
        source: "canonical",
        recordKind: decoded.kind,
        raw: stored.raw,
      }));
    }
    const retired = provider.readRetiredDisclosures();
    if (retired.kind === "unavailable") {
      return unavailable("export", retired.reason);
    }
    if (retired.kind !== "missing") {
      records.push(Object.freeze({
        source: "retired-ui-settings",
        recordKind: retired.kind === "ready" ? "retired" : "retiredInvalid",
        raw: retired.raw,
      }));
    }
    if (records.length === 0) return freezeState({ kind: "missing" });
    return Object.freeze({
      kind: "exported",
      records: Object.freeze(records),
    });
  }

  async function reset(options = {}) {
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();
    if (authoritative.kind === "unavailable") return publish(authoritative);
    if (
      expectedRevision !== undefined
      && (
        (authoritative.kind === "missing" && expectedRevision !== null)
        || (authoritative.kind === "ready" && expectedRevision !== authoritative.revision)
      )
    ) {
      return conflict(expectedRevision, authoritative);
    }

    // Remove the retired input first. If canonical removal then fails, the
    // still-authoritative v1 document prevents an old decision resurfacing.
    const retired = provider.removeRetiredDisclosures();
    if (retired.kind === "unavailable") {
      return publish(unavailable(retired.operation, retired.reason, authoritative));
    }
    const removed = provider.removeDocument();
    if (removed.kind === "unavailable") {
      return publish(unavailable(removed.operation, removed.reason, authoritative));
    }
    return publish({ kind: "missing", raw: null });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Notice disclosure repository listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeInvalidation = provider.subscribeInvalidation(() => load());

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribeInvalidation();
    listeners.clear();
    provider.dispose();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: NOTICE_DISCLOSURE_SCHEMA_VERSION,
    dispose,
    exportRecord,
    load,
    reset,
    setOpen,
    state,
    subscribe,
  });
}

export function createBrowserNoticeDisclosurePreferenceRepository() {
  return createNoticeDisclosurePreferenceRepository({
    provider: createBrowserNoticeDisclosureLocalProvider(),
  });
}
